<script lang="ts">
  /**
   * The setup of a round on one device, by taking turns
   * (`docs/decisions/0059`): the shell's `PickerPage`, opened by the Together
   * tab's "On this device" tile.
   *
   * Reading down: who is playing (the shell's `PlayerNames`, two to four
   * seats, a blank seat playing as "Player 2"; a signed-in device owner's
   * name fills the first seat once, and stays editable), then the same
   * questions the solo picker asks (`ModeQuestions.svelte`: mode, goal,
   * hardness). Start names what it will start, and waits while two seats
   * share a name. The names and the choices are kept while the app is open,
   * so "End the round" lands back here as it was left.
   *
   * No personal best and no record is shown: a round on one device is kept
   * apart from both.
   */
  import { Icon, PickerPage, PlayerNames, SectionLabel } from '@artibens/app-shell';
  import { playerNamesValid, resolvePlayerNames } from '@artibens/app-shell';
  import type { ModeConfig } from '@interlock/modes';
  import { LOCAL_NAME_MAX_LENGTH, LOCAL_PLAYERS_MAX, LOCAL_PLAYERS_MIN } from '@interlock/protocol';

  import { startAudioForGesture } from '../audio/bootstrap.js';
  import { account } from '../state/account.svelte.js';
  import ModeQuestions from './ModeQuestions.svelte';
  import { configFromChoices, defaultModeChoices, describeChoice } from './mode-choices.js';
  import type { ModeChoices } from './mode-choices.js';

  const {
    open,
    onclose,
    onStart,
  }: {
    open: boolean;
    onclose: () => void;
    /** The names as they will play (resolved), in turn order, and the round's mode. */
    onStart: (players: readonly string[], config: ModeConfig) => void;
  } = $props();

  /** Seat `index`'s default, 0-based: what a blank seat plays as. */
  function seatName(index: number): string {
    return `Player ${String(index + 1)}`;
  }

  let names: string[] = $state(['', '']);
  let choices: ModeChoices = $state(defaultModeChoices());
  /** The device owner's name goes in once, the first time the page opens signed in. */
  let prefilled = false;

  $effect(() => {
    if (!open || prefilled) return;
    if (account.status !== 'signed-in' || account.player === null) return;
    prefilled = true;
    if (names[0] === '') {
      names[0] = Array.from(account.player.username).slice(0, LOCAL_NAME_MAX_LENGTH).join('');
    }
  });

  const valid = $derived(playerNamesValid(names, seatName));
  const config = $derived(configFromChoices(choices));
  const startLabel = $derived(
    `Start · ${String(names.length)} players · ${describeChoice(config)}`,
  );

  function start(): void {
    if (!valid) return;
    // The AudioContext may only start on a user gesture; this tap is it.
    startAudioForGesture();
    onclose();
    onStart(resolvePlayerNames(names, seatName), config);
  }
</script>

{#snippet sections()}
  <SectionLabel variant="picker" as="h3">Who is playing?</SectionLabel>
  <PlayerNames
    value={names}
    onchange={(next) => {
      names = next;
    }}
    min={LOCAL_PLAYERS_MIN}
    max={LOCAL_PLAYERS_MAX}
    maxLength={LOCAL_NAME_MAX_LENGTH}
    label="Who is playing?"
    fieldLabel={seatName}
    defaultName={seatName}
    addLabel="Add a player"
    removeLabel={(name) => `Remove ${name}`}
    duplicateMessage="Two players have this name. Change one of them."
    testid="local-names"
  />
  <ModeQuestions bind:choices />
{/snippet}

{#snippet playIcon()}<Icon name="play" size={18} />{/snippet}

<PickerPage
  {open}
  title="On this device"
  {onclose}
  {sections}
  note="Take turns on one phone, tablet or computer: everyone plays the same pieces, one after another. Kept on this device, never in your scores or the records."
  start={{
    label: startLabel,
    icon: playIcon,
    disabled: !valid,
    onclick: start,
    testid: 'local-start',
  }}
  testid="local-setup"
  testids={{ note: 'local-setup-note' }}
/>
